import { Form, Input, FormData, Select, Hidden, FormRow, FormCol, CheckboxList } from '@alxgrn/react-form';
import { SelectOption } from '@alxgrn/react-form/dist/components/Select';
import React, { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from '../hooks/store';
import { Catalog } from '../types';
import { CheckboxListOption } from '@alxgrn/react-form/dist/components/CheckboxList';
import { post, put } from '../utils/api';
import { authUpdateUserCategories } from '../store/authSlice';
import Modal from '../components/common/Modal';
import Translate from '../components/common/Translate';

type CatalogResponse = {
    catalog: Catalog;
};

interface CategoryFormProps {
    category?: Catalog;
    catalog?: Catalog[];
    isOpen: boolean;
    onClose: () => void;
};

const LIMIT_LONG = 100;
const LIMIT_SHORT = 20;

const CategoryModal: FC<CategoryFormProps> = ({ category, catalog, isOpen, onClose }) => {
    const dispatch = useAppDispatch();
	const { t, i18n } = useTranslation();
    const lang = i18n.language === 'en' ? 'en' : 'ru';
    const name = i18n.language === 'en' ? 'name_en' : 'name_ru';
    const user = useAppSelector(store => store.auth.user);
    const event = useAppSelector(store => store.event);
    const categories = useAppSelector(store => store.categories);
	const [ quantity_ru, setQuantityRu ] = useState(category?.quantity_ru ?? '');
	const [ quantity_en, setQuantityEn ] = useState(category?.quantity_en ?? '');
	const [ comments_ru, setCommentsRu ] = useState(category?.comments_ru ?? '');
	const [ comments_en, setCommentsEn ] = useState(category?.comments_en ?? '');
    const [ options, setOptions ] = useState<SelectOption[]>([]);
    const [ category_id, setCategoryId ] = useState('');
    const [ isTranslating, setIsTranslating ] = useState(false);
    const [ price_segment, setPriceSegment ] = useState<CheckboxListOption[]>([]);
    const [ error, setError ] = useState('');
    const [ isLoading, setIsLoading ] = useState(false);
    /**
     * Сбрасываем параметры
     */
    useEffect(() => {
        setQuantityRu(category?.quantity_ru ?? '');
        setQuantityEn(category?.quantity_en ?? '');
        setCommentsRu(category?.comments_ru ?? '');
        setCommentsEn(category?.comments_en ?? '');
    }, [ category ]);
    /**
     * Оставим для выбора только те категории, которые еще не выбраны
     */ 
    useEffect(() => {
        if(!catalog) return;
        const options: SelectOption[] = [];
        for(let i = 0; i < categories.length; i ++) {
            if(catalog.find(c => c.category_id === categories[i].id)) continue;
            options.push({ value: categories[i].id + '', option: categories[i][name] });
        }
        options.sort((a, b) => {
            return (a.option).localeCompare(b.option);
        });
        setOptions(options);
        // Сбрасываем выбор категории
        setCategoryId('');
    }, [ name, categories, catalog ]);
    /**
     * Подготовим чекбоксы для выбора ценового сегмента
     */
    useEffect(() => {
        const price_segment = category?.price_segment ?? [ false, false, false ];
        setPriceSegment([{
            label: t('category.price_segment_economy'),
            value: 'economy',
            checked: price_segment[0],
        },{
            label: t('category.price_segment_medium'),
            value: 'medium',
            checked: price_segment[1],
        },{
            label: t('category.price_segment_premium'),
            value: 'premium',
            checked: price_segment[2],
        }]);
    }, [ category, t ]);
    /**
     * Отправка данных
     */
    const onSubmit = async (data: FormData) => {
		setIsLoading(true);
        setError('');
        // если ценовой сегмент придет, то он будет в виде массива из слов, надо его переделать в формат хранения
        const data_segment: string[] = data.price_segment ? data.price_segment as string[] : [];
        const price_segment: boolean[] = [
            data_segment.includes('economy'),
            data_segment.includes('medium'),
            data_segment.includes('premium'),
        ];
        // отправляем
        let result;
        if (category) {
            result = await put('/catalog', { ...data, price_segment });
        } else {
		    result = await post('/catalog', { ...data, price_segment });
        }
		if(result.error) {
			setError(result.error.text[lang]);
		} 
        if(result.data) {
            // Обновим информацию о пользователе в сторе
            if(user && !category) {
                const category_id = (result.data as CatalogResponse).catalog.category_id;
                const newCategories = [...user.categories];
                const index = newCategories.indexOf(category_id);
                if(index < 0) {
                    newCategories.push(category_id);
                    dispatch(authUpdateUserCategories(newCategories));
                }
            }
            // Закроем модалку
            onClose();
		}
        setIsLoading(false);
	};
    /**
     * Проверка того, что данные были измененеы
     */
    const wasChanged = () => {
        return (
            quantity_ru !== (category?.quantity_ru ?? '') ||
            quantity_en !== (category?.quantity_en ?? '') ||
            comments_ru !== (category?.comments_ru ?? '') ||
            comments_en !== (category?.comments_en ?? '') ||
            price_segment[0].checked !== (category?.price_segment[0] ?? false) ||
            price_segment[1].checked !== (category?.price_segment[1] ?? false) ||
            price_segment[2].checked !== (category?.price_segment[2] ?? false) ||
            (!category && !isNaN(parseInt(category_id)))
        );
    };

    const beforeClose = () => {
        if(isLoading) return;
        if (wasChanged() && !window.confirm(t('category.discard_confirm') ?? undefined)) return;
        onClose();
    };

    if(!user || !event) return null;
    if(catalog && !options.length) return null;

    return (
    <Modal isOpen={isOpen} onClose={beforeClose}>

        {category
        ? <h1>{category[name]}</h1>
        : <h1>{t('category.' + user.role + '.title')}</h1>}
        
        {isLoading ? <div>{t('common.loading')}</div> :
        <Form
            error={error}
            submit={isTranslating ? undefined : category ? t('category.submit') : t('category.' + user.role + '.submit')}
            onSubmit={onSubmit}
        >
            {!category &&
            <FormRow>
                <FormCol>
                    <Select
                        id='category_id'
                        label={t('category.' + user.role + '.category')}
                        value={category_id}
                        options={options}
                        onChange={setCategoryId}
                        disabled={isTranslating}
                        required={true}
                        placeholder={t('category.hint_category')}
                    />
                </FormCol>
            </FormRow>}

            <FormRow>
                <FormCol>
                    <Input
                        id='quantity_ru'
                        type='text'
                        value={quantity_ru}
                        onChange={setQuantityRu}
                        label={t('category.' + user.role + '.quantity_ru')}
                        limit={LIMIT_SHORT}
                        disabled={isTranslating}
                        top={t('category.hint_quantity_ru')}
                        bottom={<Translate
                                    current={quantity_ru}
                                    text={quantity_en}
                                    from='en'
                                    limit={LIMIT_SHORT}
                                    setText={setQuantityRu}
                                    setIsTranslating={setIsTranslating}
                                />}
                    />
                </FormCol>
                <FormCol>
                    <Input
                        id='quantity_en'
                        type='text'
                        value={quantity_en}
                        onChange={setQuantityEn}
                        label={t('category.' + user.role + '.quantity_en')}
                        limit={LIMIT_SHORT}
                        disabled={isTranslating}
                        top={t('category.hint_quantity_en')}
                        bottom={<Translate
                                    current={quantity_en}
                                    text={quantity_ru}
                                    from='ru'
                                    limit={LIMIT_SHORT}
                                    setText={setQuantityEn}
                                    setIsTranslating={setIsTranslating}
                                />}
                    />
                </FormCol>
            </FormRow>
            <FormRow>
                <FormCol>
                    <Input
                        id='comments_ru'
                        type='text'
                        value={comments_ru}
                        onChange={setCommentsRu}
                        label={t('category.' + user.role + '.comments_ru')}
                        limit={LIMIT_LONG}
                        disabled={isTranslating}
                        bottom={<Translate
                                    current={comments_ru}
                                    text={comments_en}
                                    from='en'
                                    limit={LIMIT_LONG}
                                    setText={setCommentsRu}
                                    setIsTranslating={setIsTranslating}
                                />}
                    />
                </FormCol>
                <FormCol>
                    <Input
                        id='comments_en'
                        type='text'
                        value={comments_en}
                        onChange={setCommentsEn}
                        label={t('category.' + user.role + '.comments_en')}
                        limit={LIMIT_LONG}
                        disabled={isTranslating}
                        bottom={<Translate
                                    current={comments_en}
                                    text={comments_ru}
                                    from='ru'
                                    limit={LIMIT_LONG}
                                    setText={setCommentsEn}
                                    setIsTranslating={setIsTranslating}
                                />}
                    />
                </FormCol>
            </FormRow>
            {event.use_price_segment &&
            <FormRow>
                <FormCol>
                    <CheckboxList
						id='price_segment'
						label={t('category.price_segment')}
						required={user.role === 'seller'}
						options={price_segment}
						onChange={setPriceSegment}
						disabled={isTranslating}
					/>
                </FormCol>
            </FormRow>}

            <Hidden id='attendee_id' value={user.id}/>
            {category && <Hidden id='category_id' value={category.category_id}/>}
        </Form>}
	</Modal>);
};

export default CategoryModal;
